import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CampaignIcon } from '../../assets';
import { PERMISSIONS } from '../../config/constants';
import { replaceIds, ROUTES } from '../../config/routes/config';
import { CREATE_CAMPAIGN_RECURRENT } from '../../graphql/campaign/mutation';
import { usePermissions } from '../../hooks/usePermissions';
import type { ResCreateCampaignRecurrentType } from '../../types';
import { Button } from '../common/Button';
import { BrandImage } from '../images/BrandImage';
import { BrandGroupDropdown } from '../inputs/BrandGroupDropdown';
import type { ClientBrandItem } from '../inputs/ClientSearchPanel';
import { ClientSearchPanel } from '../inputs/ClientSearchPanel';
import { TextInput } from '../inputs/TextInput';
import { UserDropdownList } from '../inputs/UserDropdownList';
import { PopupLayout } from './PopupLayout';

type TCreateCampaignForm = {
  name: string;
  influencers: Array<{ value: string; id: string }>;
  accountManagers: Array<{ value: string; id: string }>;
  group_id: string;
};

export const CreateCampaignRecurrent = ({ refetch }: { refetch: any }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const { checkPermission } = usePermissions();
  const [selectedBrand, setSelectedBrand] = useState<ClientBrandItem | null>(
    null,
  );

  const { ...methods } = useForm<TCreateCampaignForm>();
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const [createCampaignRecurrent, { loading }] =
    useMutation<ResCreateCampaignRecurrentType>(CREATE_CAMPAIGN_RECURRENT);

  const onSubmit = (data: TCreateCampaignForm) => {
    if (!selectedBrand) return;
    createCampaignRecurrent({
      variables: {
        name: data.name,
        brand_id: selectedBrand.id,
        influencers: data.influencers.map((i) => i.value) || [],
        accountManagers: data.accountManagers.map((i) => i.value) || [],
        group_id: data.group_id,
      },
      onCompleted: (res) => {
        navigate(
          replaceIds({
            url: ROUTES.SINGLE_CAMPAIGN_RECURRENT_SETTINGS_INFLUENCER,
            params: [
              {
                influencerId: data.influencers[0].value || '',
              },
              { campaignId: res.createCampaignRecurrent.data || '' },
            ],
          }),
        );
        reset();
        setOpen(false);
        setSelectedBrand(null);
        refetch();
      },
      onError: () => {},
    });
  };

  const closeModal = () => {
    reset();
    setOpen(false);
    setSelectedBrand(null);
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        style="light-blue"
        text={t('campaigns.createCampaign')}
        iconLeft={<CampaignIcon />}
        customClassnames={`${!checkPermission(PERMISSIONS.CREATE_CAMPAIGN_RECURRENT) && 'hidden'}`}
      />
      {open && (
        <PopupLayout
          title={t('campaigns.createCampaignTitle')}
          closeModal={closeModal}
          icon={<CampaignIcon />}
        >
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-row gap-x-6 mt-5 items-stretch"
            >
              {/* LEFT — Client search (only clients, no prospects) */}
              <ClientSearchPanel
                selectedBrand={selectedBrand}
                onSelect={setSelectedBrand}
                control={control}
                categoryFieldName="__clientCategory"
              />

              {/* RIGHT — Campaign recurrent details */}
              <div className="flex flex-col gap-y-4 w-1/2">
                {/* Selected client read-only */}
                <div className="flex flex-col gap-y-1">
                  <p className="text-white font-semibold text-sm">
                    {t('campaigns.createClient')}
                  </p>
                  <div className="flex flex-row items-center gap-x-3 bg-white rounded-[4px] px-3 py-2 min-h-[41px]">
                    {selectedBrand ? (
                      <>
                        <BrandImage
                          image={selectedBrand.profile_picture}
                          size="size-[24px]"
                        />
                        <span className="text-dark-blue text-sm font-medium">
                          {selectedBrand.name}
                        </span>
                      </>
                    ) : (
                      <span className="text-[#5A5A5A] text-sm">
                        {t('campaigns.createClientPlaceholder')}
                      </span>
                    )}
                  </div>
                </div>

                <TextInput
                  register={register}
                  registerName="name"
                  type="text"
                  label={t('campaigns.createName')}
                  placeholder={t('campaigns.createNamePlaceholder')}
                  validation={{ required: t('campaigns.nameRequired') }}
                  errors={errors.name}
                />

                <BrandGroupDropdown
                  brand_id={selectedBrand?.id || undefined}
                  label={t('singleCampaign.assignGroup')}
                  name="group_id"
                  control={control}
                  isClearable={false}
                  validation={{ required: t('campaigns.groupRequired') }}
                  error={errors.group_id?.message}
                />

                <UserDropdownList
                  type="influentia"
                  accessName="accountManagers"
                  label={t('campaigns.createAccountManagers')}
                  accessKey="value"
                  addText={t('campaigns.createAccountManagersAdd')}
                  placeholderSelects={t(
                    'campaigns.createAccountManagersPlaceholder',
                  )}
                  control={control}
                  minLength={0}
                />

                <UserDropdownList
                  type="influencer"
                  accessName="influencers"
                  label={t('campaigns.createInfluencers')}
                  accessKey="value"
                  addText={t('campaigns.createInfluencersAdd')}
                  placeholderSelects={t(
                    'campaigns.createInfluencersPlaceholder',
                  )}
                  control={control}
                  error={
                    errors.influencers?.root
                      ? t('campaigns.addInfluencerError')
                      : ''
                  }
                  menuPosition="fixed"
                />

                <div className="flex justify-end mt-auto">
                  <div className="w-[150px]">
                    <Button
                      style="light-blue"
                      text={t('global.save')}
                      customClassnames="w-full justify-center"
                      type="submit"
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </PopupLayout>
      )}
    </>
  );
};
